import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { ThemeMode } from '@/stores/theme';
import { useThemeStore } from '@/stores/theme';
import { Monitor, Moon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const MODES: { value: ThemeMode; icon: typeof Sun; labelKey: string }[] = [
  { value: 'light', icon: Sun, labelKey: 'nav.theme_light' },
  { value: 'dark', icon: Moon, labelKey: 'nav.theme_dark' },
  { value: 'system', icon: Monitor, labelKey: 'nav.theme_system' },
];

export function ThemeMenu() {
  const { t } = useTranslation();
  const mode = useThemeStore((s) => s.mode);
  const setMode = useThemeStore((s) => s.setMode);
  const current = MODES.find((m) => m.value === mode) ?? MODES[2];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {/* Sun and moon cross-fade in place, as in the reference: the control
            shows the switch this click will make, and the dropdown holds the
            three-way choice. */}
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('nav.theme')}
          title={t(current.labelKey)}
          className="scale-95 rounded-full"
        >
          <Sun className="size-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
          <Moon className="absolute size-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-36">
        <DropdownMenuRadioGroup value={mode} onValueChange={(v) => setMode(v as ThemeMode)}>
          {MODES.map(({ value, icon: Icon, labelKey }) => (
            <DropdownMenuRadioItem key={value} value={value}>
              <Icon aria-hidden className="size-3.5 text-muted-foreground" />
              {t(labelKey)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
